// Player page
// - Finds a player by :playerId and role === PLAYER
// - Shows Spinner only while the record could still arrive
// - Renders <h1> error if player is not found

import { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PlayerCard from '../components/PlayerCard';
import Spinner from '../components/Spinner';
import { useUser } from '../utils/hooks';
import { UsersRoles } from '../utils/enums';

export default function PagePlayer() {
  const { playerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { entity: found, isResolved } = useUser(playerId);

  const editParam = searchParams.get('edit');
  const teamIdParam = searchParams.get('teamid');

  // If the user opens "new" route without ?edit param, redirect them back to the
  // previous route. A team is required too: the form only opens from a roster,
  // and a player created without one lands outside every roster
  useEffect(() => {
    if (playerId === 'new') {
      if (!editParam || !teamIdParam) navigate(-1, { replace: true });
    }
  }, [playerId, editParam, teamIdParam, navigate]);

  // Case: creating a new player with ?edit and ?teamid present
  if (playerId === 'new') {
    // If redirect just triggered, keep UI calm with a spinner
    if (!editParam || !teamIdParam) {
      return (
        <Layout screenSized className='page-player'>
          <Spinner show />
        </Layout>
      );
    }

    // Render creation mode
    return (
      <Layout screenSized className='page-player' trail>
        <PlayerCard player={{ id: 'new', teams: [teamIdParam] }} />
      </Layout>
    );
  }

  // Find the player by id and role
  const player = found?.role === UsersRoles.PLAYER ? found : undefined;

  // Spinner only while the record could still show up — the current user's own
  // card is already in usersById, so it renders without waiting for the list
  if (!player && !isResolved) {
    return (
      <Layout screenSized className='page-player'>
        <Spinner show />
      </Layout>
    );
  }

  // Not found => plain H1 error
  if (!player) {
    return (
      <Layout screenSized className='page-player'>
        <h1>{`Error: no route matches url "/players/${playerId}"`}</h1>
      </Layout>
    );
  }

  // Happy path
  return (
    <Layout className='page-player' trail>
      <PlayerCard player={player} />
    </Layout>
  );
}
